import type { MetaFunction } from "@remix-run/cloudflare";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link } from "@remix-run/react";
import {
  X, FlipHorizontal2, Play, Pause,
  Plus, Minus, Trash2, Download, Check, Camera, Scissors, Move,
  AlignLeft, AlignCenter, AlignRight, Sun, Moon, Mic,
} from "lucide-react";
import styles from "~/styles/teleprompter.module.css";
import { QuickCut } from "~/components/QuickCut";
import AppAuthGate, { isAppAuthenticated } from "~/components/AppAuthGate";
import { SplashScreen } from "~/components/SplashScreen";

export const meta: MetaFunction = () => [
  { title: "Teleprompter Cam — Prodigium Dynamics" },
  { name: "description", content: "Record with a built-in teleprompter. Hold to record, release to pause." },
];

type Phase = "camera" | "preview" | "editor";

// ── HELPERS ───────────────────────────────────────────────────────────────────

function getMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "video/webm";
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? "video/webm";
}

function fmtClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

async function stitchTakes(
  blobs: Blob[],
  mimeType: string,
  onProgress: (frac: number) => void,
  isCancelled: () => boolean,
): Promise<Blob> {
  if (!blobs.length) throw new Error("No takes to stitch");

  const createdUrls: string[] = [];
  const revokeAll = () => createdUrls.forEach((u) => URL.revokeObjectURL(u));

  let audioCtx: AudioContext | null = null;
  let rafId = 0;
  let rec: MediaRecorder | null = null;

  try {
    const probe = document.createElement("video");
    probe.muted = true;
    probe.src = URL.createObjectURL(blobs[0]);
    createdUrls.push(probe.src);
    await new Promise<void>((r) => { probe.onloadedmetadata = () => r(); });
    const W = probe.videoWidth || 1280;
    const H = probe.videoHeight || 720;

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d")!;

    const video = document.createElement("video");
    video.playsInline = true;

    audioCtx = new AudioContext();
    await audioCtx.resume().catch(() => {});
    const dest = audioCtx.createMediaStreamDestination();
    const srcNode = audioCtx.createMediaElementSource(video);
    srcNode.connect(dest);

    const canvasStream = canvas.captureStream(30);
    const mixed = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...dest.stream.getAudioTracks(),
    ]);

    const recMime = MediaRecorder.isTypeSupported(mimeType) ? mimeType : "video/webm";
    rec = new MediaRecorder(mixed, { mimeType: recMime });
    const chunks: BlobPart[] = [];
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    rec.start(200);

    const draw = () => {
      ctx.drawImage(video, 0, 0, W, H);
      rafId = requestAnimationFrame(draw);
    };

    for (let i = 0; i < blobs.length; i++) {
      if (isCancelled()) break;
      const url = URL.createObjectURL(blobs[i]);
      createdUrls.push(url);
      video.src = url;
      await new Promise<void>((r) => { video.onloadedmetadata = () => r(); });
      video.ontimeupdate = () => {
        const frac = video.duration ? video.currentTime / video.duration : 0;
        onProgress((i + Math.min(1, frac)) / blobs.length);
      };
      try {
        await video.play();
      } catch {
        video.muted = true;
        await video.play().catch(() => {});
      }
      rafId = requestAnimationFrame(draw);
      await new Promise<void>((r) => { video.onended = () => r(); });
      cancelAnimationFrame(rafId);
      video.ontimeupdate = null;
      onProgress((i + 1) / blobs.length);
    }

    rec.stop();
    await new Promise<void>((r) => { rec.onstop = () => r(); });

    return new Blob(chunks, { type: "video/webm" });
  } finally {
    cancelAnimationFrame(rafId);
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* already stopped */ }
    }
    if (audioCtx) audioCtx.close().catch(() => {});
    revokeAll();
  }
}

async function patchWebmDuration(blob: Blob, durationMs: number): Promise<Blob> {
  if (!durationMs || !isFinite(durationMs) || !blob.type.includes("webm")) return blob;
  try {
    const mod = await import("fix-webm-duration");
    const fix = (mod as { default?: typeof import("fix-webm-duration").default }).default
      ?? (mod as unknown as typeof import("fix-webm-duration").default);
    return await fix(blob, durationMs);
  } catch {
    return blob;
  }
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────

export default function TeleprompterCam() {
  const [authReady, setAuthReady] = useState(() => isAppAuthenticated("Teleprompter Cam"));

  const mimeType = useMemo(getMimeType, []);

  // Feature detection
  const recorderSupported = useMemo(() => typeof MediaRecorder !== "undefined", []);

  // DOM refs
  const videoRef    = useRef<HTMLVideoElement>(null);
  const previewRef  = useRef<HTMLVideoElement>(null);
  const scrollRef   = useRef<HTMLDivElement>(null);
  const scrollOffsetRef = useRef(0);
  const panelRef    = useRef<HTMLDivElement>(null);

  // MediaStream / MediaRecorder refs
  const streamRef   = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef   = useRef<BlobPart[]>([]);
  const segmentsRef = useRef<Blob[]>([]);
  const segDursRef  = useRef<number[]>([]);
  const holdStartRef = useRef<number>(0);
  const rafRef      = useRef<number>(0);

  // Audio VU meter refs
  const audioCtxRef   = useRef<AudioContext | null>(null);
  const analyserRef   = useRef<AnalyserNode | null>(null);
  const dataArrayRef  = useRef<Uint8Array | null>(null);
  const vuRafRef      = useRef<number>(0);

  // Safety / race-condition refs
  const isMountedRef     = useRef(true);
  const isStartingRef    = useRef(false);
  const countdownRef     = useRef<ReturnType<typeof setInterval> | null>(null);

  // UI state
  const [phase,          setPhase]          = useState<Phase>("camera");
  const [cameraReady,    setCameraReady]    = useState(false);
  const [permDenied,     setPermDenied]     = useState(false);
  const [facingMode,     setFacingMode]     = useState<"user" | "environment">("user");
  const [isHolding,      setIsHolding]      = useState(false);
  const [segmentCount,   setSegmentCount]   = useState(0);
  const [script,         setScript]         = useState("");
  const [showEditor,     setShowEditor]     = useState(false);
  const [scrollSpeed,    setScrollSpeed]    = useState(1.5);
  const [isScrolling,    setIsScrolling]    = useState(false);
  const [finalUrl,       setFinalUrl]       = useState("");
  const [finalBlob,      setFinalBlob]      = useState<Blob | null>(null);
  const [finalSegments,  setFinalSegments]  = useState(0);
  const [takes,          setTakes]          = useState<number[]>([]);
  const [currentTakeMs,  setCurrentTakeMs]  = useState(0);
  const [stitching,      setStitching]      = useState(false);
  const [stitchProgress, setStitchProgress] = useState(0);

  // Teleprompter text styling state
  const [fontSize,   setFontSize]   = useState(23);
  const [textAlign,  setTextAlign]  = useState<"left" | "center" | "right">("left");
  const [invertColors, setInvertColors] = useState(false);

  // Focus mode + countdown + VU meter state
  const [countdown,      setCountdown]      = useState<number | null>(null);
  const [hasRecordedOnce, setHasRecordedOnce] = useState(false);
  const [audioLevel,     setAudioLevel]     = useState(0);

  // Floating prompter panel box
  const [box, setBox] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const dragRef   = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);
  const resizeRef = useRef<{ px: number; py: number; ow: number; oh: number } | null>(null);

  // Battery warning
  const [batteryLevel,    setBatteryLevel]    = useState<number | null>(null);
  const [batteryCharging, setBatteryCharging] = useState(true);
  const [batteryDismissed, setBatteryDismissed] = useState(false);

  // Filmstrip selected take
  const [selectedTake,   setSelectedTake]   = useState<number | null>(null);
  const [flashActive,    setFlashActive]    = useState(false);

  // Speech recognition (for live subtitle generation)
  const speechRef = useRef<SpeechRecognition | null>(null);
  const speechSegmentsRef = useRef<{text: string; startMs: number; endMs: number}[]>([]);
  const speechStartRef = useRef<number>(0);
  const scriptRef = useRef<string>("");
  const [speechSupported, setSpeechSupported] = useState(false);

  // ── Script similarity helper (improves transcription accuracy) ──────────────

  function normalizeText(s: string): string {
    return s.toLowerCase().replace(/[^\w\sáéíóúüñ]/g, "").replace(/\s+/g, " ").trim();
  }

  // Optimised Levenshtein with 1-D array + early abort on very long strings.
  function levenshtein(a: string, b: string): number {
    const MAX_LEN = 120;
    const sa = a.length > MAX_LEN ? a.slice(0, MAX_LEN) : a;
    const sb = b.length > MAX_LEN ? b.slice(0, MAX_LEN) : b;
    const m = sa.length, n = sb.length;
    if (m === 0) return n;
    if (n === 0) return m;
    // Use a single Uint16Array to avoid nested-array GC churn.
    const prev = new Uint16Array(n + 1);
    const curr = new Uint16Array(n + 1);
    for (let j = 0; j <= n; j++) prev[j] = j;
    for (let i = 1; i <= m; i++) {
      curr[0] = i;
      for (let j = 1; j <= n; j++) {
        curr[j] = sa[i - 1] === sb[j - 1]
          ? prev[j - 1]
          : 1 + Math.min(prev[j], curr[j - 1], prev[j - 1]);
      }
      // swap rows
      for (let j = 0; j <= n; j++) prev[j] = curr[j];
    }
    return prev[n];
  }

  function similarity(a: string, b: string): number {
    const dist = levenshtein(a, b);
    const maxLen = Math.max(a.length, b.length);
    return maxLen === 0 ? 1 : 1 - dist / maxLen;
  }

  function findBestScriptMatch(transcript: string, scriptText: string): { text: string; score: number } {
    const normTranscript = normalizeText(transcript);
    // Split script into sentences/phrases
    const phrases = scriptText
      .split(/[.!?\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 3);

    let best = { text: transcript, score: 0 };

    for (const phrase of phrases) {
      const normPhrase = normalizeText(phrase);
      if (normPhrase.length < 3) continue;
      const score = similarity(normTranscript, normPhrase);
      if (score > best.score) {
        best = { text: phrase, score };
      }
    }

    // Also try with shorter sliding windows of the script
    const words = scriptText.split(/\s+/).filter((w) => w.length > 0);
    const transcriptWordCount = normTranscript.split(/\s+/).length;
    const windowSize = Math.max(3, Math.min(transcriptWordCount + 3, words.length));

    for (let i = 0; i <= words.length - windowSize; i++) {
      const window = words.slice(i, i + windowSize).join(" ");
      const normWindow = normalizeText(window);
      const score = similarity(normTranscript, normWindow);
      if (score > best.score) {
        best = { text: window, score };
      }
    }

    return best;
  }

  function correctTranscript(transcript: string, scriptText: string): string {
    if (!scriptText || scriptText.trim().length < 10) return transcript;
    const match = findBestScriptMatch(transcript, scriptText);
    // Only correct if similarity is high enough (> 0.5 = 50% similar)
    if (match.score > 0.5 && match.score < 1) {
      return match.text;
    }
    return transcript;
  }

  // ── Lifecycle: mounted flag ───────────────────────────────────────────────

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ── Safe state setters (no-op if unmounted) ───────────────────────────────

  const safeSet = useCallback(<T,>(setter: (v: T) => void, value: T) => {
    if (isMountedRef.current) setter(value);
  }, []);

  // ── Speech Recognition helpers ────────────────────────────────────────────

  const isSpeechSupported = useMemo(() => {
    if (typeof window === "undefined") return false;
    return "SpeechRecognition" in window || "webkitSpeechRecognition" in window;
  }, []);

  useEffect(() => {
    setSpeechSupported(isSpeechSupported);
  }, [isSpeechSupported]);

  const startSpeechRecognition = useCallback(() => {
    if (!isSpeechSupported || speechRef.current) return;
    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;

    // Capture current script for correction
    scriptRef.current = script;

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = false;
    // Use browser language or auto-detect; fallback to empty string for auto
    const browserLang = typeof navigator !== "undefined" ? navigator.language : "";
    recognition.lang = browserLang || ""; // empty string lets browser auto-detect in some implementations
    speechStartRef.current = performance.now();
    speechSegmentsRef.current = [];

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          let text = result[0].transcript.trim();
          if (text) {
            // Correct transcript against teleprompter script for higher accuracy
            text = correctTranscript(text, scriptRef.current);
            const now = performance.now();
            const lastSeg = speechSegmentsRef.current[speechSegmentsRef.current.length - 1];
            const startMs = lastSeg ? lastSeg.endMs : 0;
            speechSegmentsRef.current.push({
              text,
              startMs,
              endMs: now - speechStartRef.current,
            });
          }
        }
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // Ignore 'aborted' and 'no-speech' errors (common when pausing)
      if (event.error === "aborted" || event.error === "no-speech") return;
      console.warn("Speech recognition error:", event.error);
    };

    recognition.onend = () => {
      // Auto-restart if still recording (unless component unmounted)
      if (isMountedRef.current && isHolding && speechRef.current === recognition) {
        try { recognition.start(); } catch { /* already started */ }
      }
    };

    try {
      recognition.start();
      speechRef.current = recognition;
    } catch {
      speechRef.current = null;
    }
  }, [isSpeechSupported, isHolding]);

  const stopSpeechRecognition = useCallback(() => {
    const recognition = speechRef.current;
    if (!recognition) return;
    speechRef.current = null;
    try { recognition.stop(); } catch { /* already stopped */ }
  }, []);

  // ── Countdown cleanup helper ──────────────────────────────────────────────

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    safeSet(setCountdown, null);
    isStartingRef.current = false;
  }, [safeSet]);

  // ── Camera ────────────────────────────────────────────────────────────────

  const startCamera = useCallback(async () => {
    // Always clear any active countdown before switching camera
    clearCountdown();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    analyserRef.current = null;
    safeSet(setAudioLevel, 0);
    safeSet(setCameraReady, false);
    safeSet(setPermDenied, false);

    let acquiredStream: MediaStream | null = null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      acquiredStream = stream;
      if (!isMountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.warn("Video play failed:", playErr);
          videoRef.current.muted = true;
          await videoRef.current.play().catch(() => {});
        }
      }
      // Init audio analyser for VU meter
      const audioCtx = new AudioContext();
      await audioCtx.resume().catch(() => {});
      const src = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
      safeSet(setCameraReady, true);
    } catch (err) {
      acquiredStream?.getTracks().forEach((t) => t.stop());
      if (!isMountedRef.current) return;
      const name = err instanceof Error ? err.name : "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        safeSet(setPermDenied, true);
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        console.warn("Camera not found");
      } else {
        safeSet(setPermDenied, true);
      }
    }
  }, [facingMode, clearCountdown, safeSet]);

  useEffect(() => {
    startCamera();
    return () => {
      clearCountdown();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
        try { videoRef.current.load(); } catch {}
      }
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
      analyserRef.current = null;
      dataArrayRef.current = null;
      cancelAnimationFrame(vuRafRef.current);
    };
  }, [startCamera, clearCountdown]);

  // ── VU Meter loop (RMS volume, not frequency) ─────────────────────────────
  // Throttled to ~15 fps and only commits state on meaningful change to avoid
  // React re-render churn on every animation frame.

  useEffect(() => {
    if (!analyserRef.current || !dataArrayRef.current) return;
    let frameCount = 0;
    let lastLevel = -1;
    const tick = () => {
      const analyser = analyserRef.current;
      const data = dataArrayRef.current;
      if (!analyser || !data || !isMountedRef.current) return;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const x = (data[i] - 128) / 128.0;
        sum += x * x;
      }
      const rms = Math.sqrt(sum / data.length);
      const level = Math.min(100, Math.round(rms * 100 * 2.8));
      frameCount++;
      if (frameCount >= 4 && Math.abs(level - lastLevel) > 2) {
        lastLevel = level;
        frameCount = 0;
        safeSet(setAudioLevel, level);
      }
      vuRafRef.current = requestAnimationFrame(tick);
    };
    vuRafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(vuRafRef.current);
  }, [cameraReady, safeSet]);

  // ── Teleprompter scroll ───────────────────────────────────────────────────

  useEffect(() => {
    if (!isScrolling) { cancelAnimationFrame(rafRef.current); return; }
    const tick = () => {
      if (scrollRef.current) {
        scrollOffsetRef.current += scrollSpeed;
        scrollRef.current.style.transform = `translateY(-${scrollOffsetRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isScrolling, scrollSpeed]);

  // ── Live take timer ───────────────────────────────────────────────────────
  // setInterval at 100 ms instead of RAF to avoid React re-render storm.

  useEffect(() => {
    if (!isHolding) return;
    const tick = () => {
      safeSet(setCurrentTakeMs, performance.now() - holdStartRef.current);
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [isHolding, safeSet]);

  // ── Battery Status API ────────────────────────────────────────────────────

  useEffect(() => {
    if (typeof navigator === "undefined" || !("getBattery" in navigator)) return;
    let battery: { level: number; charging: boolean; addEventListener: Function; removeEventListener: Function } | null = null;

    const onLevel   = () => { if (battery && isMountedRef.current) { safeSet(setBatteryLevel, battery.level); safeSet(setBatteryDismissed, false); } };
    const onCharge  = () => { if (battery && isMountedRef.current) safeSet(setBatteryCharging, battery.charging); };

    (navigator as any).getBattery().then((b: any) => {
      battery = b;
      if (!isMountedRef.current) return;
      safeSet(setBatteryLevel, b.level);
      safeSet(setBatteryCharging, b.charging);
      b.addEventListener("levelchange",    onLevel);
      b.addEventListener("chargingchange", onCharge);
    }).catch(() => {});

    return () => {
      if (battery) {
        battery.removeEventListener("levelchange",    onLevel);
        battery.removeEventListener("chargingchange", onCharge);
      }
    };
  }, [safeSet]);

  const batteryPct = batteryLevel !== null ? Math.round(batteryLevel * 100) : null;
  const showBatteryWarn = phase === "camera"
    && !batteryCharging
    && batteryPct !== null
    && batteryPct < 15
    && !batteryDismissed;
  const batteryCritical = batteryPct !== null && batteryPct < 8;

  // ── Floating prompter: initial box + drag/resize ───────────────────────────

  const computePrompterBox = useCallback(() => {
    if (typeof window === "undefined") return { x: 0, y: 0, w: 0, h: 0 };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isLandscape = vw > vh;

    const padX = 14;
    const padTop = 64;
    const bottomReserve = 174; // bottom bar + filmstrip + safe gaps

    let w: number;
    let h: number;

    if (isLandscape) {
      // Landscape: moderate width, ~40% height so it doesn't dominate
      w = Math.min(vw * 0.62, 780);
      h = Math.min(vh - padTop - bottomReserve, vh * 0.40);
    } else {
      // Portrait: nearly full width, ~35% height as requested
      w = Math.min(vw - padX * 2, 680);
      h = Math.min(vh - padTop - bottomReserve, vh * 0.35);
    }

    const minW = 260;
    const minH = 200;
    w = Math.max(Math.round(w), minW);
    h = Math.max(Math.round(h), minH);

    const x = Math.round((vw - w) / 2);
    // Start closer to the top so it doesn't sit in the middle of the screen
    const y = Math.round(padTop + 10);

    return { x, y, w, h };
  }, []);

  useEffect(() => {
    setBox(computePrompterBox());
    const onLayout = () => setBox(computePrompterBox());
    window.addEventListener("resize", onLayout);
    window.addEventListener("orientationchange", onLayout);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("orientationchange", onLayout);
    };
  }, [computePrompterBox]);

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

  const onDragDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, ox: box.x, oy: box.y };
  };
  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || !panelRef.current) return;
    const { px, py, ox, oy } = dragRef.current;
    const nx = clamp(ox + (e.clientX - px), 4, window.innerWidth - box.w - 4);
    const ny = clamp(oy + (e.clientY - py), 8, window.innerHeight - box.h - 4);
    panelRef.current.style.left = `${nx}px`;
    panelRef.current.style.top = `${ny}px`;
  };
  const onDragUp = () => {
    if (dragRef.current && panelRef.current) {
      const nx = parseInt(panelRef.current.style.left || `${box.x}`, 10);
      const ny = parseInt(panelRef.current.style.top || `${box.y}`, 10);
      setBox((b) => ({ ...b, x: nx, y: ny }));
    }
    dragRef.current = null;
  };

  const onResizeDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeRef.current = { px: e.clientX, py: e.clientY, ow: box.w, oh: box.h };
  };
  const onResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeRef.current || !panelRef.current) return;
    e.stopPropagation();
    const { px, py, ow, oh } = resizeRef.current;
    const nw = clamp(ow + (e.clientX - px), 200, window.innerWidth - box.x - 4);
    const nh = clamp(oh + (e.clientY - py), 140, window.innerHeight - box.y - 4);
    panelRef.current.style.width = `${nw}px`;
    panelRef.current.style.height = `${nh}px`;
  };
  const onResizeUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (resizeRef.current && panelRef.current) {
      const nw = parseInt(panelRef.current.style.width || `${box.w}`, 10);
      const nh = parseInt(panelRef.current.style.height || `${box.h}`, 10);
      setBox((b) => ({ ...b, w: nw, h: nh }));
    }
    resizeRef.current = null;
  };

  // ── Preview URL cleanup ────────────────────────────────────────────────────

  useEffect(() => () => { if (finalUrl) URL.revokeObjectURL(finalUrl); }, [finalUrl]);

  // ── Recording ─────────────────────────────────────────────────────────────

  const startSegment = () => {
    if (!streamRef.current || isHolding || !recorderSupported) return;
    chunksRef.current = [];
    holdStartRef.current = performance.now();
    safeSet(setCurrentTakeMs, 0);
    const rec = new MediaRecorder(streamRef.current, { mimeType });
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.start(100);
    recorderRef.current = rec;
    safeSet(setIsHolding, true);
    safeSet(setHasRecordedOnce, true);
    // Start speech recognition on first take
    if (!speechRef.current && speechSupported) {
      startSpeechRecognition();
    }
  };

  const stopSegment = useCallback(
    (discard = false): Promise<void> =>
      new Promise((resolve) => {
        const rec = recorderRef.current;
        const dur = performance.now() - holdStartRef.current;
        if (!rec) { resolve(); return; }
        rec.onstop = () => {
          if (!discard) {
            const blob = new Blob(chunksRef.current, { type: mimeType });
            if (blob.size > 500) {
              segmentsRef.current.push(blob);
              segDursRef.current.push(dur);
              safeSet(setSegmentCount, segmentsRef.current.length);
              safeSet(setTakes, [...segDursRef.current]);
            }
          }
          chunksRef.current = [];
          resolve();
        };
        rec.stop();
        recorderRef.current = null;
        safeSet(setIsHolding, false);
        safeSet(setCurrentTakeMs, 0);
        isStartingRef.current = false;
      }),
    [mimeType, safeSet],
  );

  const deleteSegment = (index: number) => {
    if (index < 0 || index >= segmentsRef.current.length) return;
    segmentsRef.current.splice(index, 1);
    segDursRef.current.splice(index, 1);
    safeSet(setSegmentCount, segmentsRef.current.length);
    safeSet(setTakes, [...segDursRef.current]);
    if (selectedTake === index) safeSet(setSelectedTake, null);
    else if (selectedTake !== null && selectedTake > index) safeSet(setSelectedTake, selectedTake - 1);
  };

  const deleteLastSegment = () => {
    if (isHolding) {
      stopSegment(true);
      return;
    }
    if (segmentsRef.current.length > 0) {
      deleteSegment(segmentsRef.current.length - 1);
    }
  };

  const cancelStitchRef = useRef(false);

  const finalize = async () => {
    if (isHolding) await stopSegment();
    stopSpeechRecognition();
    const blobs = segmentsRef.current;
    if (blobs.length === 0) return;
    safeSet(setIsScrolling, false);
    cancelStitchRef.current = false;

    const totalDur = segDursRef.current.reduce((a, b) => a + b, 0);

    let combined: Blob;
    if (blobs.length === 1) {
      combined = await patchWebmDuration(blobs[0], segDursRef.current[0] ?? 0);
    } else {
      safeSet(setStitching, true);
      safeSet(setStitchProgress, 0);
      try {
        const stitched = await stitchTakes(blobs, mimeType, (frac) =>
          safeSet(setStitchProgress, Math.round(frac * 95)),
          () => cancelStitchRef.current,
        );
        combined = await patchWebmDuration(stitched, totalDur);
        safeSet(setStitchProgress, 100);
      } catch (err) {
        console.error("stitch error:", err);
        combined = new Blob(blobs, { type: "video/webm" });
      }
      safeSet(setStitching, false);
    }

    const url = URL.createObjectURL(combined);
    safeSet(setFinalBlob, combined);
    safeSet(setFinalSegments, blobs.length);
    safeSet(setFinalUrl, url);
    safeSet(setPhase, "preview");
  };

  const discard = () => {
    clearCountdown();
    URL.revokeObjectURL(finalUrl);
    safeSet(setFinalUrl, "");
    safeSet(setFinalBlob, null);
    segmentsRef.current = [];
    segDursRef.current = [];
    speechSegmentsRef.current = [];
    safeSet(setSegmentCount, 0);
    safeSet(setFinalSegments, 0);
    safeSet(setTakes, []);
    safeSet(setCurrentTakeMs, 0);
    safeSet(setHasRecordedOnce, false);
    safeSet(setSelectedTake, null);
    stopSpeechRecognition();
    safeSet(setPhase, "camera");
  };

  const downloadVideo = () => {
    const a = document.createElement("a");
    a.href = finalUrl;
    a.download = `teleprompter-${Date.now()}.webm`;
    a.click();
  };

  // ── Countdown + Hold handlers ─────────────────────────────────────────────

  const beginRecording = () => {
    if (!streamRef.current || isStartingRef.current) return;
    isStartingRef.current = true;
    startSegment();
  };

  const startCountdownAndRecord = () => {
    if (!streamRef.current || isStartingRef.current) return;
    beginRecording();
  };

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    (e.currentTarget).setPointerCapture(e.pointerId);
    if (isStartingRef.current) return;
    startCountdownAndRecord();
  };
  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    stopSegment();
  };

  // ── Photo ─────────────────────────────────────────────────────────────────

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    safeSet(setFlashActive, true);
    setTimeout(() => safeSet(setFlashActive, false), 120);
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(20);
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d")!;
    if (facingMode === "user") {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `teleprompter-photo-${Date.now()}.jpg`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, "image/jpeg", 0.92);
  };

  // ── Render: editor phase ──────────────────────────────────────────────────

  if (!authReady) {
    return <AppAuthGate appName="Teleprompter Cam" appTag="GRABADORA" onAuth={() => setAuthReady(true)} />;
  }

  if (phase === "editor" && finalBlob) {
    return (
      <QuickCut
        blob={finalBlob}
        speechSegments={speechSegmentsRef.current}
        onBack={() => safeSet(setPhase, "preview")}
      />
    );
  }

  // ── Render: preview phase ─────────────────────────────────────────────────

  if (phase === "preview" && finalUrl) {
    const dur = segDursRef.current.reduce((a, b) => a + b, 0);
    const sizeMB = finalBlob ? (finalBlob.size / 1024 / 1024).toFixed(1) : "0";
    return (
      <div className={styles.root}>
        <div className={styles.previewRoot}>
          <div className={styles.previewTopBar}>
            <button className={styles.iconBtn} onClick={discard} aria-label="Descartar">
              <X size={18} strokeWidth={1.5} />
            </button>
            <span className={styles.previewTitle}>Vista previa</span>
            <div style={{ width: 36 }} />
          </div>

          <video
            ref={previewRef}
            src={finalUrl}
            controls
            playsInline
            className={styles.previewVideo}
          />

          <div className={styles.previewMeta}>
            <span>{fmtClock(dur)}</span>
            <span>·</span>
            <span>{finalBlob?.type.includes("webm") ? "WebM" : "MP4"}</span>
            <span>·</span>
            <span>{sizeMB} MB</span>
          </div>

          <div className={styles.previewActions}>
            <button className={styles.discardBtn} onClick={discard}>
              <X size={13} strokeWidth={1.5} /> Descartar
            </button>
            <button className={styles.downloadBtn} onClick={downloadVideo}>
              <Download size={13} strokeWidth={1.5} /> Descargar
            </button>
          </div>

          <button className={styles.editBtn} onClick={() => safeSet(setPhase, "editor")}>
            <Scissors size={13} strokeWidth={1.5} /> Editar rápido
          </button>

          <p className={styles.previewNote}>
            {finalSegments} toma{finalSegments !== 1 ? "s" : ""} · descarga local
          </p>
        </div>
      </div>
    );
  }

  // ── Render: MediaRecorder not supported ────────────────────────────────────

  if (!recorderSupported) {
    return (
      <div className={styles.root}>
        <div className={styles.permissionWrap}>
          <Camera size={36} strokeWidth={0.9} style={{ color: "rgba(255,255,255,0.25)" }} />
          <p className={styles.permTitle}>Navegador no compatible</p>
          <p className={styles.permDesc}>
            Tu navegador no soporta MediaRecorder. Usá Chrome, Edge o Safari actualizado.
          </p>
          <Link to="/" className={styles.permBack}>← Volver al launcher</Link>
        </div>
      </div>
    );
  }

  // ── Render: permission denied ──────────────────────────────────────────────

  if (permDenied) {
    return (
      <div className={styles.root}>
        <div className={styles.permissionWrap}>
          <Camera size={36} strokeWidth={0.9} style={{ color: "rgba(255,255,255,0.25)" }} />
          <p className={styles.permTitle}>Acceso a cámara requerido</p>
          <p className={styles.permDesc}>
            Permite el acceso a la cámara y al micrófono en la configuración de tu navegador y recarga la página.
          </p>
          <Link to="/" className={styles.permBack}>← Volver al launcher</Link>
        </div>
      </div>
    );
  }

  // ── Render: main camera view ───────────────────────────────────────────────

  const totalMs = takes.reduce((a, b) => a + b, 0) + (isHolding ? currentTakeMs : 0);
  const textDynamicStyle: React.CSSProperties = { fontSize: `${fontSize}px`, textAlign };

  // Progress ring for record button (0-60s mapped to 0-100%)
  const progressPct = isHolding ? Math.min(100, (currentTakeMs / 60000) * 100) : 0;
  const circumference = 2 * Math.PI * 36; // r=36
  const strokeDashoffset = circumference - (progressPct / 100) * circumference;

  return (
    <div className={`${styles.root} ${isHolding ? styles.rootRecording : ""}`}>
      <SplashScreen name="Teleprompter Cam" tag="GRABADORA" />

      {/* Camera preview */}
      <video ref={videoRef} className={styles.camera} autoPlay playsInline muted />

      {/* Photo flash overlay */}
      {flashActive && <div className={styles.flashOverlay} />}

      {/* Stitching overlay */}
      {stitching && (
        <div className={styles.stitchOverlay}>
          <div className={styles.stitchCard}>
            <p className={styles.stitchLabel}>Uniendo tomas…</p>
            <div className={styles.stitchTrack}>
              <div className={styles.stitchFill} style={{ width: `${stitchProgress}%` }} />
            </div>
            <p className={styles.stitchPct}>{stitchProgress}%</p>
          </div>
        </div>
      )}

      {/* Bottom-right floating controls — above the hold hint */}
      <div className={`${styles.bottomRightControls} ${isHolding ? styles.bottomRightControlsHidden : ""}`}>
        <Link to="/" className={styles.iconBtn} aria-label="Salir">
          <X size={18} strokeWidth={1.5} />
        </Link>
        <button className={styles.photoBtn} onClick={takePhoto} aria-label="Tomar foto">
          <Camera size={15} strokeWidth={1.6} />
        </button>
        <button className={styles.iconBtn} onClick={() => setFacingMode((f) => f === "user" ? "environment" : "user")} aria-label="Flip cámara">
          <FlipHorizontal2 size={18} strokeWidth={1.5} />
        </button>
      </div>

      {/* REC badge */}
      {isHolding && (
        <div className={styles.recBadge}>
          <span className={styles.recDot} /> REC
        </div>
      )}

      {/* Battery warning banner */}
      {showBatteryWarn && (
        <div className={`${styles.batteryBanner} ${batteryCritical ? styles.batteryBannerCritical : ""}`}>
          <span className={styles.batteryBannerText}>
            {batteryCritical
              ? `⚠ Batería crítica (${batteryPct}%) — guarda lo que tienes, el sistema puede cortar.`
              : `Batería baja (${batteryPct}%) — la grabación puede lagear.`
            }
          </span>
          {!batteryCritical && (
            <button className={styles.batteryBannerDismiss} onClick={() => safeSet(setBatteryDismissed, true)} aria-label="Cerrar aviso">
              <X size={11} strokeWidth={2} />
            </button>
          )}
        </div>
      )}

      {/* Floating teleprompter panel */}
      {box.w > 0 && (
        <div
          ref={panelRef}
          className={`${styles.prompterPanel} ${invertColors ? styles.prompterPanelLight : ""} ${isHolding ? styles.prompterPanelFocus : ""}`}
          style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
        >
          {/* Drag handle */}
          <div
            className={`${styles.prompterDragBar} ${isHolding ? styles.prompterDragBarFocus : ""}`}
            onPointerDown={onDragDown}
            onPointerMove={onDragMove}
            onPointerUp={onDragUp}
            onPointerCancel={onDragUp}
          >
            <Move size={11} strokeWidth={1.6} />
            <span className={styles.prompterDragLabel}>TELEPROMPTER</span>
            <span className={styles.prompterDragHint}>arrastra</span>
          </div>

          {/* Scrollable text with reading guide */}
          <div ref={scrollRef} className={styles.prompterScroll}>
            <div className={styles.readingGuide} />
            {script
              ? <p className={styles.prompterText} style={textDynamicStyle}>{script}</p>
              : <p className={styles.prompterPlaceholder}>Escribí tu guion acá → tocá el botón 📝 abajo</p>
            }
          </div>

          {/* Controls — 3 clusters, hidden during recording */}
          <div className={`${styles.prompterControls} ${isHolding ? styles.prompterControlsHidden : ""}`}>
            {/* Cluster 1: Playback */}
            <div className={styles.ctrlCluster}>
              <button
                className={`${styles.pillBtn} ${isScrolling ? styles.pillBtnActive : ""}`}
                onClick={() => setIsScrolling((v) => !v)}
              >
                {isScrolling ? <Pause size={11} strokeWidth={1.5} /> : <Play size={11} strokeWidth={1.5} />}
                {isScrolling ? "Pausar" : "Scroll"}
              </button>
              <div className={styles.speedRow}>
                <button className={styles.speedBtn} onClick={() => setScrollSpeed((s) => Math.max(0.5, parseFloat((s - 0.5).toFixed(1))))}>
                  <Minus size={9} strokeWidth={2} />
                </button>
                <span className={styles.speedVal}>{scrollSpeed.toFixed(1)}×</span>
                <button className={styles.speedBtn} onClick={() => setScrollSpeed((s) => Math.min(8, parseFloat((s + 0.5).toFixed(1))))}>
                  <Plus size={9} strokeWidth={2} />
                </button>
              </div>
            </div>

            <div className={styles.ctrlDivider} />

            {/* Cluster 2: Text styling */}
            <div className={styles.ctrlCluster}>
              <button className={styles.miniBtn} onClick={() => setFontSize((s) => Math.max(14, s - 2))} aria-label="Reducir fuente">
                A<Minus size={7} strokeWidth={2.5} />
              </button>
              <span className={styles.miniVal}>{fontSize}</span>
              <button className={styles.miniBtn} onClick={() => setFontSize((s) => Math.min(56, s + 2))} aria-label="Aumentar fuente">
                A<Plus size={7} strokeWidth={2.5} />
              </button>

              <div className={styles.alignGroup}>
                <button className={`${styles.alignBtn} ${textAlign === "left" ? styles.alignBtnActive : ""}`} onClick={() => setTextAlign("left")} aria-label="Alinear izquierda">
                  <AlignLeft size={11} strokeWidth={1.5} />
                </button>
                <button className={`${styles.alignBtn} ${textAlign === "center" ? styles.alignBtnActive : ""}`} onClick={() => setTextAlign("center")} aria-label="Centrar">
                  <AlignCenter size={11} strokeWidth={1.5} />
                </button>
                <button className={`${styles.alignBtn} ${textAlign === "right" ? styles.alignBtnActive : ""}`} onClick={() => setTextAlign("right")} aria-label="Alinear derecha">
                  <AlignRight size={11} strokeWidth={1.5} />
                </button>
              </div>

              <button className={`${styles.pillBtn} ${invertColors ? styles.pillBtnActive : ""}`} onClick={() => setInvertColors((v) => !v)} aria-label={invertColors ? "Modo oscuro" : "Modo claro"}>
                {invertColors ? <Sun size={11} strokeWidth={1.5} /> : <Moon size={11} strokeWidth={1.5} />}
              </button>
            </div>

            <div className={styles.ctrlDivider} />

            {/* Cluster 3: Actions */}
            <div className={styles.ctrlCluster}>
              <button className={styles.pillBtn} onClick={() => setShowEditor((v) => !v)}>
                📝 Guion
              </button>
              {script && (
                <button className={styles.pillBtn} onClick={() => { scrollOffsetRef.current = 0; if (scrollRef.current) scrollRef.current.style.transform = ""; }}>
                  ↺ Top
                </button>
              )}
            </div>
          </div>

          {/* Resize handle */}
          <div
            className={styles.prompterResize}
            onPointerDown={onResizeDown}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeUp}
            onPointerCancel={onResizeUp}
            aria-label="Redimensionar"
          />
        </div>
      )}

      {/* Script editor panel */}
      {showEditor && (
        <div className={styles.scriptPanel}>
          <div className={styles.scriptPanelHeader}>
            <span className={styles.scriptPanelTitle}>GUION</span>
            <button className={styles.iconBtn} onClick={() => setShowEditor(false)} aria-label="Cerrar editor">
              <Check size={16} strokeWidth={1.8} />
            </button>
          </div>
          <textarea
            className={styles.scriptTextarea}
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder={"Escribí o pegá tu guion acá...\n\nCada línea aparecerá en el teleprompter."}
            rows={9}
            autoFocus
          />
        </div>
      )}

      {/* Filmstrip */}
      {(takes.length > 0 || isHolding) && (
        <div className={`${styles.filmstrip} ${isHolding ? styles.filmstripCompact : ""}`}>
          <div className={styles.filmstripTrack}>
            {takes.map((d, i) => (
              <div
                key={i}
                className={`${styles.filmstripCard} ${selectedTake === i ? styles.filmstripCardSelected : ""}`}
                style={{ minWidth: Math.max(56, d / 1000 * 28) }}
                onClick={() => setSelectedTake(i === selectedTake ? null : i)}
              >
                <span className={styles.filmstripNum}>T{i + 1}</span>
                <span className={styles.filmstripDur}>{fmtClock(d)}</span>
                {selectedTake === i && (
                  <button className={styles.filmstripDeleteBtn} onClick={(e) => { e.stopPropagation(); deleteSegment(i); }} aria-label={`Eliminar toma ${i + 1}`}>
                    <X size={10} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            ))}
            {isHolding && (
              <div className={`${styles.filmstripCard} ${styles.filmstripCardLive}`} style={{ minWidth: Math.max(56, currentTakeMs / 1000 * 28) }}>
                <span className={styles.filmstripNumLive}>REC</span>
                <span className={styles.filmstripDurLive}>{fmtClock(currentTakeMs)}</span>
              </div>
            )}
          </div>
          <span className={styles.filmstripTotal}>{fmtClock(totalMs)}</span>
        </div>
      )}

      {/* Bottom bar */}
      <div className={styles.bottomBar}>
        {/* Delete last */}
        <button className={styles.sideBtn} onClick={deleteLastSegment} disabled={segmentCount === 0 && !isHolding} aria-label={isHolding ? "Descartar clip actual" : "Eliminar último clip"} title={isHolding ? "Descartar clip actual" : `Eliminar clip ${segmentCount}`}>
          <Trash2 size={16} strokeWidth={1.5} />
          {segmentCount > 0 && !isHolding && (
            <span className={styles.sideBtnBadge}>{segmentCount}</span>
          )}
        </button>

        {/* VU Meter */}
        <div className={styles.vuMeter}>
          <div className={styles.vuTrack}>
            <div
              className={styles.vuFill}
              style={{
                height: `${audioLevel}%`,
                background: audioLevel > 85 ? "rgb(255, 60, 60)" : audioLevel > 60 ? "rgb(255, 200, 40)" : "rgb(40, 220, 100)",
              }}
            />
          </div>
          <Mic size={9} strokeWidth={1.8} className={styles.vuIcon} />
        </div>

        {/* Record button with progress ring */}
        <div className={styles.recordBtnWrap}>
          {isHolding && (
            <svg className={styles.recordRing} viewBox="0 0 80 80">
              <circle
                className={styles.recordRingBg}
                cx="40" cy="40" r="36"
              />
              <circle
                className={styles.recordRingProgress}
                cx="40" cy="40" r="36"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                style={{ transition: "stroke-dashoffset 0.3s linear" }}
              />
            </svg>
          )}
          <button
            className={`${styles.recordBtn} ${isHolding ? styles.recordBtnActive : ""}`}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            aria-label="Mantén presionado para grabar"
          >
            <span className={styles.recordBtnInner} />
          </button>
        </div>

        {/* Finalize */}
        <button className={styles.sideBtn} onClick={finalize} disabled={segmentCount === 0} aria-label="Finalizar y previsualizar" title="Finalizar grabación">
          <Check size={16} strokeWidth={2} />
        </button>
      </div>

      {/* Hint for first-time users */}
      {!isHolding && segmentCount === 0 && cameraReady && !hasRecordedOnce && (
        <p className={styles.holdHint}>Mantén presionado para grabar</p>
      )}

    </div>
  );
}
